/**
 * PDF Generation Status Indicator
 *
 * Shows PDF generation progress as a status badge + text indicator.
 * Similar to GenerationProgressIndicator for LLM generation.
 *
 * States:
 * - Queued: Waiting to start
 * - Processing: Currently generating
 * - Failed: Error occurred
 *
 * Note: Completed state is handled separately in JobResumesTab with success alert.
 */

import { Badge, Group, Loader, Text, Alert } from '@mantine/core';
import { IconFileTypePdf, IconX } from '@tabler/icons-react';
import { PdfGenerationJob } from '../../api/pdf';

export interface PdfGenerationStatusIndicatorProps {
  generationJob: PdfGenerationJob | null | undefined;
  isLoading?: boolean;
}

export function PdfGenerationStatusIndicator({
  generationJob,
  isLoading,
}: PdfGenerationStatusIndicatorProps) {
  // Debug logging
  console.log('[PdfGenerationStatusIndicator] Rendering with job:', generationJob, 'isLoading:', isLoading);

  // If no data yet, show initial loading state
  if (!generationJob) {
    if (isLoading) {
      console.log('[PdfGenerationStatusIndicator] Waiting for first poll...');
      return (
        <Group gap="xs">
          <Badge
            size="lg"
            variant="light"
            leftSection={<Loader size={12} />}
            color="gray"
          >
            Initializing...
          </Badge>
          <Text size="sm" c="dimmed">
            Starting PDF generation job
          </Text>
        </Group>
      );
    }
    console.log('[PdfGenerationStatusIndicator] No generation job and not loading, returning null');
    return null;
  }

  const { status, error } = generationJob;
  console.log('[PdfGenerationStatusIndicator] Status:', status);

  // Queued state: waiting to start
  if (status === 'queued') {
    return (
      <Group gap="xs">
        <Badge
          size="lg"
          variant="light"
          leftSection={<IconFileTypePdf size={12} />}
          color="gray"
        >
          Queued...
        </Badge>
        <Text size="sm" c="dimmed">
          PDF is queued for generation
        </Text>
      </Group>
    );
  }

  // Processing state: currently generating
  if (status === 'processing') {
    return (
      <Group gap="xs">
        <Badge
          size="lg"
          variant="light"
          leftSection={<Loader size={12} />}
          color="blue"
        >
          Generating PDF...
        </Badge>
        <Text size="sm" c="dimmed">
          Converting markdown to PDF (this takes ~3-4 minutes)
        </Text>
      </Group>
    );
  }

  // Completed state: handled in parent component (JobResumesTab) with success alert
  // This component no longer renders completed state

  // Failed state: error occurred
  if (status === 'failed') {
    return (
      <Alert
        icon={<IconX size={16} />}
        color="red"
        title="PDF Generation Failed"
        mt="md"
        mb="md"
      >
        <Text size="sm">{error || 'An error occurred while generating the PDF. Please try again.'}</Text>
      </Alert>
    );
  }

  return null;
}

/**
 * Format file size in human-readable format
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}
